// Response schemas for recurring checklists and their runs (plan §4 /checklists, /checklist-runs;
// spec §5.6). Shapes follow apps/api/src/modules/checklists/view.ts; unknown keys are dropped.
// Leads text is already redacted by the API for team roles (constitution §6.4); the client parses.
import { ROLES } from '@cis/shared';
import { z } from 'zod';

// Pack labels (title, section, who) are up to 500 characters; item text and cadence (free text in
// the pack, a TEXT column in the database) are up to 20,000 (review G4-R1).
// For team roles the API swaps each Leads id ("R1") for "[Leads only]" after those caps, so the
// caps here leave the same headroom as the task schemas (review B3-R1, D-R24).
const REDACTION_HEADROOM = 6;
const PACK_LABEL_MAX = 500;
const PACK_TEXT_MAX = 20_000;
const MAX_LABEL = PACK_LABEL_MAX * REDACTION_HEADROOM;
const MAX_TEXT = PACK_TEXT_MAX * REDACTION_HEADROOM;
const MAX_STAMP = 64;
const MAX_DATE = 10;
const MAX_PAGE_SIZE = 100;
const MAX_ITEMS = 1000;

// Checklist number as the pack writes it. Matches "01", "14"; rejects "1", "001".
// https://regex101.com/?flavor=javascript&regex=%5E%5Cd%7B2%7D%24&testString=01%0A14%0A1%0A001
export const CHECKLIST_CODE = /^\d{2}$/;

export const CHECKLIST_RESULTS = [
  'tick',
  'pass',
  'fixed',
  'action',
  'critical_fail',
  'not_checked',
] as const;
export const CHECKLIST_RUN_STATUSES = ['open', 'completed', 'signed'] as const;

export const checklistResultSchema = z.enum(CHECKLIST_RESULTS);
export const checklistRunStatusSchema = z.enum(CHECKLIST_RUN_STATUSES);

const label = z.string().max(MAX_LABEL);
const calendarDate = z.string().max(MAX_DATE);
const runId = z.number().int().positive();

export const checklistPersonSchema = z.object({
  id: z.number().int().positive(),
  name: label,
});

export const checklistSummarySchema = z.object({
  code: z.string().regex(CHECKLIST_CODE),
  title: label,
  cadence: z.string().max(MAX_TEXT),
  attesterRole: z.enum(ROLES).nullable(),
  retired: z.boolean(),
  itemCount: z.number().int().nonnegative(),
});

export const checklistItemSchema = z.object({
  no: z.number().int().positive(),
  section: label,
  text: z.string().max(MAX_TEXT),
  required: z.boolean(),
  /** tick, or a result: Pass / Fixed on the spot / Action / Critical fail / Not checked. */
  resultType: z.enum(['tick', 'result']),
  who: label,
});

export const checklistDetailSchema = checklistSummarySchema.extend({
  items: z.array(checklistItemSchema).max(MAX_ITEMS),
});

export const checklistPageSchema = z.object({
  items: z.array(checklistSummarySchema).max(MAX_PAGE_SIZE),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive().max(MAX_PAGE_SIZE),
  total: z.number().int().nonnegative(),
});

export const checklistRunSummarySchema = z.object({
  id: runId,
  checklist: z.object({ code: z.string().regex(CHECKLIST_CODE), title: label }),
  runDate: calendarDate,
  site: label.nullable(),
  runBy: checklistPersonSchema,
  status: checklistRunStatusSchema,
  signedBy: checklistPersonSchema.nullable(),
  signedAt: z.string().max(MAX_STAMP).nullable(),
  correctsRunId: runId.nullable(),
  correctedBy: z.array(runId).max(MAX_ITEMS),
  version: z.number().int().nonnegative(),
});

export const checklistItemResultSchema = z.object({
  result: checklistResultSchema,
  note: z.string().max(MAX_TEXT).nullable(),
  by: checklistPersonSchema,
  at: z.string().max(MAX_STAMP),
});

export const checklistRunItemSchema = checklistItemSchema.extend({
  result: checklistItemResultSchema.nullable(),
});

export const checklistRunViewSchema = checklistRunSummarySchema.extend({
  items: z.array(checklistRunItemSchema).max(MAX_ITEMS),
});

export const checklistRunPageSchema = z.object({
  items: z.array(checklistRunSummarySchema).max(MAX_PAGE_SIZE),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive().max(MAX_PAGE_SIZE),
  total: z.number().int().nonnegative(),
});

export type ChecklistResult = z.infer<typeof checklistResultSchema>;
export type ChecklistRunStatus = z.infer<typeof checklistRunStatusSchema>;
export type ChecklistPerson = z.infer<typeof checklistPersonSchema>;
export type ChecklistSummary = z.infer<typeof checklistSummarySchema>;
export type ChecklistItem = z.infer<typeof checklistItemSchema>;
export type ChecklistDetail = z.infer<typeof checklistDetailSchema>;
export type ChecklistPage = z.infer<typeof checklistPageSchema>;
export type ChecklistRunSummary = z.infer<typeof checklistRunSummarySchema>;
export type ChecklistItemResult = z.infer<typeof checklistItemResultSchema>;
export type ChecklistRunItem = z.infer<typeof checklistRunItemSchema>;
export type ChecklistRunView = z.infer<typeof checklistRunViewSchema>;
export type ChecklistRunPage = z.infer<typeof checklistRunPageSchema>;
